import React, { useState } from 'react';
import { Monitor, RefreshCw, Send, Smartphone } from 'lucide-react';
import { Skeleton } from '../../../../components/common/SkeletonLoader';
import { EDM_UI } from '../../../../utils/edm';
import type { EdmPreview } from '../../../../hooks/useEdmCampaigns';

/**
 * Renders ONLY the server's `preview.html`, never the editor contents, in a
 * fully sandboxed iframe (`sandbox=""`: no scripts, no same-origin, no
 * forms, no popups, no top navigation), the same as the house previews.
 */
const PreviewPane: React.FC<{
  preview: EdmPreview | null;
  loading: boolean;
  error: string | null;
  dirty: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  testEmail: string | null;
  canSendTest: boolean;
  sendingTest: boolean;
  testNotice: string | null;
  onSendTest: () => void;
}> = ({ preview, loading, error, dirty, canRefresh, onRefresh, testEmail, canSendTest, sendingTest, testNotice, onSendTest }) => {
  const [desktop, setDesktop] = useState(true);
  const toggle = (active: boolean) =>
    `px-2.5 py-1 rounded text-[11px] inline-flex items-center gap-1 ${
      active ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:text-white'
    }`;

  return (
    <div className="space-y-4 xl:sticky xl:top-4">
      <div className={EDM_UI.card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1">
            <button type="button" onClick={() => setDesktop(true)} className={toggle(desktop)}>
              <Monitor className="h-3 w-3" /> Desktop
            </button>
            <button type="button" onClick={() => setDesktop(false)} className={toggle(!desktop)}>
              <Smartphone className="h-3 w-3" /> Mobile
            </button>
          </div>
          <button type="button" onClick={onRefresh} disabled={!canRefresh || loading} className={EDM_UI.secondaryButton}>
            <RefreshCw className="h-4 w-4" /> {dirty ? 'Save & preview' : 'Refresh preview'}
          </button>
        </div>
        {loading && <Skeleton className="h-[40rem] w-full" />}
        {!loading && error && <div className={EDM_UI.errorBanner}>{error}</div>}
        {!loading && !error && preview && (
          <div className="mx-auto overflow-hidden rounded-md bg-white shadow-lg" style={{ maxWidth: desktop ? '37.5rem' : '22.5rem' }}>
            <div className="border-b border-gray-200 px-4 py-2 text-xs text-gray-600">
              <strong>Subject:</strong> {preview.subject}
            </div>
            <iframe
              title="eDM preview"
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={preview.html}
              className="min-h-[40rem] w-full border-0"
            />
          </div>
        )}
        {!loading && !error && !preview && (
          <p className="text-sm text-gray-400">Save your content, then refresh the preview to see exactly what recipients get.</p>
        )}
      </div>
      <div className={EDM_UI.card}>
        <h2 className={EDM_UI.heading}>
          <Send className={EDM_UI.headingIcon} /> Send a test
        </h2>
        <p className="text-sm text-gray-300">
          Sends to {testEmail ?? 'your sign-in email'} only. Up to 5 tests per campaign per day.
        </p>
        <button type="button" onClick={onSendTest} disabled={!canSendTest || sendingTest} className={EDM_UI.secondaryButton}>
          <Send className="h-4 w-4" /> Send test
        </button>
        {testNotice && <p className="text-sm text-amber-300">{testNotice}</p>}
      </div>
    </div>
  );
};

export default PreviewPane;
